import React, { useRef, useState, useEffect } from 'react';
import { Grid, Box, Divider, Typography, IconButton, Slider } from "@mui/material";
import * as actions from "./actions";
import { toastSuccess, toastError, copyToClipboard } from "../../utils";
import { Copy } from '../../components/Icons';
import { API_URL } from '../../constants';
import CopyableField from '../../components/CopyableField';
import ActionButton from '../../components/ActionButton';
import StatusBadge from '../../components/StatusBadge';
import ConfirmModal from '../Common/ConfirmModal';

// Custom Audio Player
function AudioPlayer({ src }) {
    const audioRef = useRef(null);
    const [playing, setPlaying] = useState(false);
    const [currentTime, setCurrent] = useState(0);
    const [duration, setDuration] = useState(0);
    const [volume, setVolume] = useState(0.8);

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;
        const onLoaded = () => setDuration(audio.duration || 0);
        const onTime = () => setCurrent(audio.currentTime);
        const onEnded = () => setPlaying(false);
        audio.addEventListener('loadedmetadata', onLoaded);
        audio.addEventListener('timeupdate', onTime);
        audio.addEventListener('ended', onEnded);
        return () => {
            audio.removeEventListener('loadedmetadata', onLoaded);
            audio.removeEventListener('timeupdate', onTime);
            audio.removeEventListener('ended', onEnded);
        };
    }, []);

    const toggle = () => {
        const audio = audioRef.current;
        if (playing) { audio.pause(); } else { audio.play(); }
        setPlaying(!playing);
    };

    const seek = (_, val) => {
        audioRef.current.currentTime = val;
        setCurrent(val);
    };

    const changeVolume = (_, val) => {
        setVolume(val);
        audioRef.current.volume = val;
    };

    const fmt = (s) => {
        if (!s || isNaN(s)) return '0:00';
        const m = Math.floor(s / 60);
        const sec = Math.floor(s % 60);
        return `${m}:${sec < 10 ? '0' : ''}${sec}`;
    };

    return (
        <Box className="custom-audio-player">
            <audio ref={audioRef} src={src} preload="metadata" />
            <IconButton onClick={toggle} className="custom-audio-player__play">
                {playing ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16" rx="1" /><rect x="14" y="4" width="4" height="16" rx="1" /></svg>
                ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><polygon points="5,3 19,12 5,21" /></svg>
                )}
            </IconButton>
            <span className="custom-audio-player__time">{fmt(currentTime)}</span>
            <Slider
                size="small"
                min={0}
                max={duration || 1}
                value={currentTime}
                onChange={seek}
                className="custom-audio-player__seek"
                sx={{
                    color: '#6C63FF',
                    mx: 1.5,
                    flex: 1,
                    '& .MuiSlider-thumb': { width: 12, height: 12 },
                    '& .MuiSlider-rail': { opacity: 0.3 },
                }}
            />
            <span className="custom-audio-player__time">{fmt(duration)}</span>
            <Slider
                size="small"
                min={0}
                max={1}
                step={0.01}
                value={volume}
                onChange={changeVolume}
                className="custom-audio-player__volume"
                sx={{
                    color: '#94A3B8',
                    width: 60,
                    ml: 2,
                    '& .MuiSlider-thumb': { width: 10, height: 10 },
                    '& .MuiSlider-rail': { opacity: 0.3 },
                }}
            />
        </Box>
    );
}

const trackStatusMap = { 0: 'in_review', 1: 'approved', 2: 'rejected', 3: 'takendown' };

function ViewSong(props) {
    const { selectedSong } = props;
    const {
        audio_id = '',
        video_isrc = '',
        audio_track = '',
        track_version = '',
        instrumental = '',
        title = '',
        version_subtitle = '',
        primary_artist1 = '',
        apple_id1 = '',
        spotify_id1 = '',
        primary_artist2 = '',
        apple_id2 = '',
        spotify_id2 = '',
        primary_artist3 = '',
        apple_id3 = '',
        spotify_id3 = '',
        featuring1 = '',
        apple_id1f = '',
        spotify_id1f = '',
        featuring2 = '',
        apple_id2f = '',
        spotify_id2f = '',
        featuring3 = '',
        apple_id3f = '',
        spotify_id3f = '',
        author1 = '',
        author2 = '',
        author3 = '',
        composer1 = '',
        composer2 = '',
        composer3 = '',
        producer = '',
        p_line = '',
        production_year = '',
        publisher = '',
        isrc_yes_no = '',
        isrc = '',
        genre = '',
        subgenre = '',
        price_tier = '',
        explicit_version = '',
        track_title_language = '',
        lyrics_language = '',
        lyrics = '',
        caller_tune = '',
        destribute_music = '',
        download_video_link = '',
        status = 0,
    } = selectedSong;

    const [confirmDelete, setConfirmDelete] = useState(false);

    const formatNameStr = (name) => {
        if (!name) return '';
        if (name.includes('|~|')) {
            const [first, last] = name.split('|~|');
            return `${first} ${last}`;
        }
        return name;
    };

    const onDelete = (item) => {
        actions
            .deleteFileFromServer({ fileLink: item })
            .then((res) => {
                toastSuccess("Audio deleted successfully.");
            })
            .catch((err) => {
                toastError(err?.message || "Something went wrong");
            });
    };

    return (
        <Box className="view-song-redesign">
            {/* Header row: title + status + ID */}
            <Box className="view-song-redesign__header">
                <Typography variant="h6" sx={{ fontWeight: 600 }}>{title || 'Untitled Track'}</Typography>
                <Box display="flex" alignItems="center" gap={1.5}>
                    <StatusBadge status={trackStatusMap[status] || 'pending'} />
                    <Typography variant="caption" sx={{ color: 'var(--text-secondary)', fontFamily: 'monospace' }}>
                        FM-AU-{audio_id}
                    </Typography>
                </Box>
            </Box>

            {/* Audio Player */}
            {audio_track && (
                <Box mt={2}>
                    <AudioPlayer src={`${API_URL}/${audio_track}`} />
                    <Box mt={1} display="flex" gap={1} justifyContent="flex-end">
                        <a
                            href={`${API_URL}/${audio_track}`}
                            download={audio_track}
                            target="_blank"
                            rel="noreferrer"
                            style={{ textDecoration: 'none' }}
                        >
                            <ActionButton variant="settle">Download Audio</ActionButton>
                        </a>
                        <ActionButton variant="danger" onClick={() => setConfirmDelete(true)}>
                            Delete Audio
                        </ActionButton>
                    </Box>
                </Box>
            )}

            <Divider sx={{ my: 2, borderColor: 'var(--border)' }} />

            {/* Track Details */}
            <Grid container spacing={3}>
                {/* Left column */}
                <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" sx={{ color: 'var(--primary)', mb: 1 }}>Track Info</Typography>
                    <CopyableField label="Title" value={title} />
                    <CopyableField label="Track Version" value={track_version} />
                    <CopyableField label="Version Subtitle" value={version_subtitle} />
                    <CopyableField label="Instrumental" value={instrumental} />
                    <CopyableField label="Genre" value={genre} />
                    <CopyableField label="Subgenre" value={subgenre} />
                    <CopyableField label="Price Tier" value={price_tier} />
                    <CopyableField label="Explicit Version" value={explicit_version} />
                    <CopyableField label="Title Language" value={track_title_language} />
                    <CopyableField label="Lyrics Language" value={lyrics_language} />
                    <CopyableField label="Caller Tune" value={caller_tune} />
                    <CopyableField label="Distribute Music" value={destribute_music} />
                </Grid>

                {/* Right column */}
                <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" sx={{ color: 'var(--primary)', mb: 1 }}>Production & Rights</Typography>
                    <CopyableField label="P Line" value={p_line} />
                    <CopyableField label="Production Year" value={production_year} />
                    <CopyableField label="Publisher" value={publisher} />
                    <CopyableField label="ISRC" value={isrc_yes_no} />
                    <CopyableField label="Audio ISRC" value={isrc} highlight="#EF4444" />
                    <CopyableField label="Video ISRC" value={video_isrc} />
                    <CopyableField label="Video Link" value={download_video_link} />
                </Grid>
            </Grid>

            {/* Artists */}
            {(primary_artist1 || featuring1) && (
                <>
                    <Divider sx={{ my: 2, borderColor: 'var(--border)' }} />
                    <Typography variant="subtitle2" sx={{ color: 'var(--primary)', mb: 1 }}>Artists</Typography>
                    <Grid container spacing={3}>
                        <Grid item xs={12} sm={6}>
                            {primary_artist1 && (
                                <Box mb={1.5}>
                                    <Typography variant="caption" sx={{ color: 'var(--text-secondary)', fontWeight: 600 }}>Primary Artist 1</Typography>
                                    <CopyableField label="Name" value={primary_artist1} />
                                    <CopyableField label="Apple ID" value={apple_id1} />
                                    <CopyableField label="Spotify ID" value={spotify_id1} />
                                </Box>
                            )}
                            {primary_artist2 && (
                                <Box mb={1.5}>
                                    <Typography variant="caption" sx={{ color: 'var(--text-secondary)', fontWeight: 600 }}>Primary Artist 2</Typography>
                                    <CopyableField label="Name" value={primary_artist2} />
                                    <CopyableField label="Apple ID" value={apple_id2} />
                                    <CopyableField label="Spotify ID" value={spotify_id2} />
                                </Box>
                            )}
                            {primary_artist3 && (
                                <Box mb={1.5}>
                                    <Typography variant="caption" sx={{ color: 'var(--text-secondary)', fontWeight: 600 }}>Primary Artist 3</Typography>
                                    <CopyableField label="Name" value={primary_artist3} />
                                    <CopyableField label="Apple ID" value={apple_id3} />
                                    <CopyableField label="Spotify ID" value={spotify_id3} />
                                </Box>
                            )}
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            {featuring1 && (
                                <Box mb={1.5}>
                                    <Typography variant="caption" sx={{ color: 'var(--text-secondary)', fontWeight: 600 }}>Featuring Artist 1</Typography>
                                    <CopyableField label="Name" value={featuring1} />
                                    <CopyableField label="Apple ID" value={apple_id1f} />
                                    <CopyableField label="Spotify ID" value={spotify_id1f} />
                                </Box>
                            )}
                            {featuring2 && (
                                <Box mb={1.5}>
                                    <Typography variant="caption" sx={{ color: 'var(--text-secondary)', fontWeight: 600 }}>Featuring Artist 2</Typography>
                                    <CopyableField label="Name" value={featuring2} />
                                    <CopyableField label="Apple ID" value={apple_id2f} />
                                    <CopyableField label="Spotify ID" value={spotify_id2f} />
                                </Box>
                            )}
                            {featuring3 && (
                                <Box mb={1.5}>
                                    <Typography variant="caption" sx={{ color: 'var(--text-secondary)', fontWeight: 600 }}>Featuring Artist 3</Typography>
                                    <CopyableField label="Name" value={featuring3} />
                                    <CopyableField label="Apple ID" value={apple_id3f} />
                                    <CopyableField label="Spotify ID" value={spotify_id3f} />
                                </Box>
                            )}
                        </Grid>
                    </Grid>
                </>
            )}

            {/* Contributors */}
            {(author1 || composer1 || producer) && (
                <>
                    <Divider sx={{ my: 2, borderColor: 'var(--border)' }} />
                    <Typography variant="subtitle2" sx={{ color: 'var(--primary)', mb: 1 }}>Contributors</Typography>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            {author1 && <CopyableField label="Lyricist 1" value={formatNameStr(author1)} />}
                            {author2 && <CopyableField label="Lyricist 2" value={formatNameStr(author2)} />}
                            {author3 && <CopyableField label="Lyricist 3" value={formatNameStr(author3)} />}
                            {composer1 && <CopyableField label="Composer 1" value={formatNameStr(composer1)} />}
                            {composer2 && <CopyableField label="Composer 2" value={formatNameStr(composer2)} />}
                            {composer3 && <CopyableField label="Composer 3" value={formatNameStr(composer3)} />}
                            {producer && <CopyableField label="Music Producer" value={producer} />}
                        </Grid>
                    </Grid>
                </>
            )}

            {/* Lyrics */}
            {lyrics && (
                <>
                    <Divider sx={{ my: 2, borderColor: 'var(--border)' }} />
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                        <Typography variant="subtitle2" sx={{ color: 'var(--primary)' }}>Lyrics</Typography>
                        <IconButton size="small" onClick={() => copyToClipboard(lyrics)} sx={{ color: 'var(--text-secondary)', '&:hover': { color: 'var(--primary)' } }}>
                            <Copy size={14} />
                        </IconButton>
                    </Box>
                    <Box sx={{
                        background: 'rgba(255,255,255,0.03)',
                        borderRadius: '6px',
                        padding: '12px 16px',
                        maxHeight: 200,
                        overflow: 'auto',
                        fontSize: '0.85rem',
                        color: 'var(--text-primary)',
                        whiteSpace: 'pre-wrap',
                        border: '1px solid var(--border)',
                    }}>
                        {lyrics}
                    </Box>
                </>
            )}
            <ConfirmModal
                open={confirmDelete}
                title="Delete Audio"
                message="Are you sure you want to delete this audio file? This action cannot be undone."
                onSubmitConfirm={() => {
                    setConfirmDelete(false);
                    onDelete(audio_track);
                }}
                handleClose={() => setConfirmDelete(false)}
            />
        </Box>
    );
}

export default ViewSong;
